import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SelectWithAdd from '@/components/SelectWithAdd';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Trophy, Dumbbell, Save, Search,
  Check, MapPin, Calendar as CalendarIcon, Users as UsersIcon
} from 'lucide-react';

const BACKEND_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");
const API = `${BACKEND_URL}/api`;

const todayISO = () => new Date().toISOString().slice(0, 10);

// Card de jugador con expansión inline al seleccionar (rating + comentarios)
const PlayerRow = ({ player: p, observed, onToggle, onUpdate, testIdPrefix }) => {
  const selected = !!observed[p.id];
  const data = observed[p.id] || {};
  return (
    <div
      data-testid={`${testIdPrefix}-${p.id}`}
      className={`rounded-lg border transition-all ${
        selected ? 'border-brand-500 bg-brand-50/40' : 'border-gray-200'
      }`}
    >
      <button
        type="button"
        onClick={() => onToggle(p.id)}
        className="w-full flex items-center gap-3 p-3 text-left active:bg-gray-50 rounded-lg"
      >
        <div className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
          selected ? 'bg-brand-600 border-brand-600' : 'border-gray-300'
        }`}>
          {selected && <Check className="w-4 h-4 text-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">
            {p.first_name} {p.last_name}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {p.position || 'Sin posición'}{p.origin_club ? ` · ${p.origin_club}` : ''}
          </p>
        </div>
      </button>
      {selected && (
        <div className="px-3 pb-3 pt-1 border-t border-brand-200/60 mt-1 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs text-gray-600">Calificación (opcional)</Label>
              <span className="text-sm font-bold text-brand-600 min-w-[3rem] text-right">
                {data.rating ? `${data.rating}/10` : 'Sin calificar'}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              step="1"
              value={data.rating || 0}
              onChange={(e) => {
                const val = e.target.value === '0' ? '' : e.target.value;
                onUpdate(p.id, 'rating', val);
              }}
              data-testid={`rating-${p.id}`}
              className="w-full h-2 accent-brand-600"
            />
          </div>
          <Textarea
            placeholder="Comentarios del día (opcional)"
            value={data.comments || ''}
            onChange={(e) => onUpdate(p.id, 'comments', e.target.value)}
            rows={2}
            className="text-sm"
            data-testid={`comments-${p.id}`}
          />
        </div>
      )}
    </div>
  );
};

const SeguimientoForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  usePageTitle(isEdit ? 'Editar seguimiento' : 'Nuevo seguimiento');
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [step, setStep] = useState(1);

  // form state
  const [eventType, setEventType] = useState('partido');
  const [date, setDate] = useState(todayISO());
  const [categoryId, setCategoryId] = useState('');
  const [homeTeam, setHomeTeam] = useState(''); // club (backend)
  const [awayTeam, setAwayTeam] = useState(''); // opponent (backend)
  const [location, setLocation] = useState('');
  const [generalNotes, setGeneralNotes] = useState('');
  // observed: { [player_id]: { rating, comments } }
  const [observed, setObserved] = useState({});

  // catalogs
  const [categories, setCategories] = useState([]);
  const [players, setPlayers] = useState([]);
  const [searchPlayer, setSearchPlayer] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [cats, playersRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/players?limit=500&scope=all`),
        ]);
        setCategories(cats.data || []);
        setPlayers(playersRes.data?.data || []);

        if (isEdit) {
          const rec = await axios.get(`${API}/tracking/${id}`);
          const r = rec.data;
          setEventType(r.event_type);
          setDate(r.date);
          setCategoryId(r.category_id || '');
          setHomeTeam(r.club || '');
          setAwayTeam(r.opponent || '');
          setLocation(r.location || '');
          setGeneralNotes(r.general_notes || '');
          const init = {};
          (r.players_observed || []).forEach((p) => {
            init[p.player_id] = { rating: p.rating || '', comments: p.comments || '' };
          });
          setObserved(init);
        }
      } catch (e) {
        console.error(e);
        toast.error('Error al cargar datos');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  // Equipos seleccionados (para filtrar)
  const selectedTeams = useMemo(() => {
    const arr = [];
    if (homeTeam) arr.push(homeTeam);
    if (eventType === 'partido' && awayTeam && awayTeam !== homeTeam) arr.push(awayTeam);
    return arr;
  }, [homeTeam, awayTeam, eventType]);

  // Jugadores sugeridos (por equipo + categoría)
  const suggestedPlayers = useMemo(() => {
    return players.filter((p) => {
      if (selectedTeams.length > 0) {
        if (!p.origin_club || !selectedTeams.includes(p.origin_club)) return false;
      }
      if (categoryId) {
        const cats = p.category_ids || (p.category_id ? [p.category_id] : []);
        if (!cats.includes(categoryId)) return false;
      }
      return true;
    });
  }, [players, selectedTeams, categoryId]);

  const searchedPlayers = useMemo(() => {
    if (!searchPlayer) return [];
    const term = searchPlayer.toLowerCase();
    return players.filter((p) => {
      const full = `${p.first_name} ${p.last_name}`.toLowerCase();
      return full.includes(term);
    }).slice(0, 30);
  }, [players, searchPlayer]);

  const selectedPlayers = useMemo(() => {
    return players.filter((p) => observed[p.id]);
  }, [players, observed]);

  const togglePlayer = (playerId) => {
    setObserved((prev) => {
      if (prev[playerId]) {
        const { [playerId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [playerId]: { rating: '', comments: '' } };
    });
  };

  const updatePlayerField = (playerId, field, value) => {
    setObserved((prev) => ({
      ...prev,
      [playerId]: { ...(prev[playerId] || {}), [field]: value },
    }));
  };

  // Validación step 1
  const canProceed = () => {
    if (!date) return false;
    if (!eventType) return false;
    return true;
  };

  const handleSave = async () => {
    const picked = Object.entries(observed);
    if (picked.length === 0) {
      return toast.error('Seleccioná al menos un jugador observado');
    }

    const payload = {
      date,
      event_type: eventType,
      category_id: categoryId || null,
      club: homeTeam || null,
      opponent: eventType === 'partido' ? (awayTeam || null) : null,
      location: location || null,
      general_notes: generalNotes || null,
      players_observed: picked.map(([player_id, v]) => ({
        player_id,
        rating: v.rating ? Number(v.rating) : null,
        comments: v.comments || null,
      })),
    };

    try {
      setSaving(true);
      if (isEdit) {
        await axios.patch(`${API}/tracking/${id}`, payload);
        toast.success('Seguimiento actualizado');
      } else {
        await axios.post(`${API}/tracking`, payload);
        toast.success('Seguimiento registrado');
      }
      navigate('/seguimiento');
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.detail || 'Error al guardar';
      toast.error(typeof msg === 'string' ? msg : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto pb-28 sm:pb-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => (step === 1 ? navigate('/seguimiento') : setStep(1))}
            data-testid="btn-back"
            className="h-11 w-11"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {isEdit ? 'Editar seguimiento' : 'Nuevo seguimiento'}
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Paso {step} de 2 · {step === 1 ? 'Datos del evento' : 'Jugadores y evaluación'}
            </p>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-5" data-testid="wizard-progress">
          <div className={`flex-1 h-1.5 rounded-full ${step >= 1 ? 'bg-brand-600' : 'bg-gray-200'}`} />
          <div className={`flex-1 h-1.5 rounded-full ${step >= 2 ? 'bg-brand-600' : 'bg-gray-200'}`} />
        </div>

        {/* ───────── STEP 1: Evento ───────── */}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-1">
            {/* Tipo - buttons grandes */}
            <Card className="p-4">
              <Label className="text-sm font-medium text-gray-700 mb-3 block">
                ¿Qué vas a ir a ver?
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setEventType('partido')}
                  data-testid="event-type-partido"
                  className={`flex flex-col items-center justify-center gap-2 py-6 rounded-xl border-2 transition-all ${
                    eventType === 'partido'
                      ? 'border-amber-500 bg-amber-50 text-amber-700 shadow-md'
                      : 'border-gray-200 text-gray-600 active:bg-gray-50'
                  }`}
                >
                  <Trophy className="w-8 h-8" />
                  <span className="font-semibold text-base">Partido</span>
                </button>
                <button
                  type="button"
                  onClick={() => setEventType('entrenamiento')}
                  data-testid="event-type-entrenamiento"
                  className={`flex flex-col items-center justify-center gap-2 py-6 rounded-xl border-2 transition-all ${
                    eventType === 'entrenamiento'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-md'
                      : 'border-gray-200 text-gray-600 active:bg-gray-50'
                  }`}
                >
                  <Dumbbell className="w-8 h-8" />
                  <span className="font-semibold text-base">Práctica</span>
                </button>
              </div>
            </Card>

            {/* Fecha */}
            <Card className="p-4">
              <Label htmlFor="date" className="text-sm font-medium text-gray-700 mb-2 block">
                <CalendarIcon className="inline w-4 h-4 mr-1 -mt-0.5" />
                Fecha
              </Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="input-date"
                className="h-12 text-base"
              />
            </Card>

            {/* Equipo(s) */}
            <Card className="p-4 space-y-3">
              <Label className="text-sm font-medium text-gray-700 block">
                <UsersIcon className="inline w-4 h-4 mr-1 -mt-0.5" />
                {eventType === 'partido' ? 'Equipos que se enfrentan' : 'Equipo que vas a ver'}
              </Label>
              <div>
                <Label htmlFor="home-team" className="text-xs text-gray-500">
                  {eventType === 'partido' ? 'Equipo local' : 'Equipo'}
                </Label>
                <SelectWithAdd
                  catalogType="teams"
                  value={homeTeam}
                  onChange={setHomeTeam}
                  placeholder="Elegir equipo..."
                  testId="input-home-team"
                  addLabel="Agregar equipo"
                  className="mt-1"
                />
              </div>
              {eventType === 'partido' && (
                <div>
                  <Label htmlFor="away-team" className="text-xs text-gray-500">Equipo visitante</Label>
                  <SelectWithAdd
                    catalogType="teams"
                    value={awayTeam}
                    onChange={setAwayTeam}
                    placeholder="Elegir equipo..."
                    testId="input-away-team"
                    addLabel="Agregar equipo"
                    className="mt-1"
                  />
                </div>
              )}

              {selectedTeams.length > 0 && (
                <p className="text-xs text-gray-500 pt-1">
                  Se te sugerirán los jugadores de {selectedTeams.length === 1 ? 'este equipo' : 'ambos equipos'} en el próximo paso.
                </p>
              )}
            </Card>

            {/* Categoría + Lugar (opcionales, colapsables) */}
            <Card className="p-4 space-y-3">
              <div>
                <Label htmlFor="category" className="text-sm text-gray-700">Categoría (opcional)</Label>
                <Select value={categoryId || 'none'} onValueChange={(v) => setCategoryId(v === 'none' ? '' : v)}>
                  <SelectTrigger id="category" data-testid="select-category" className="h-12 text-base mt-1">
                    <SelectValue placeholder="Todas las categorías" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Todas las categorías</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="location" className="text-sm text-gray-700">
                  <MapPin className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                  Lugar (opcional)
                </Label>
                <Input
                  id="location"
                  placeholder="Ej: Estadio Belvedere"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  data-testid="input-location"
                  className="h-12 text-base mt-1"
                />
              </div>
            </Card>
          </div>
        )}

        {/* ───────── STEP 2: Jugadores ───────── */}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-2">
            {/* Resumen del evento */}
            <Card className="p-3 bg-gray-50">
              <div className="flex items-center gap-2 text-sm">
                {eventType === 'partido' ? (
                  <Trophy className="w-4 h-4 text-amber-600" />
                ) : (
                  <Dumbbell className="w-4 h-4 text-emerald-600" />
                )}
                <span className="font-medium">
                  {homeTeam || 'Sin equipo'}
                  {eventType === 'partido' && awayTeam && <span className="text-gray-500"> vs {awayTeam}</span>}
                </span>
                <span className="text-gray-400">·</span>
                <span className="text-gray-500">{date}</span>
                <span className="ml-auto">
                  <Badge variant="secondary" data-testid="count-selected" className="text-sm">
                    {selectedPlayers.length} {selectedPlayers.length === 1 ? 'seleccionado' : 'seleccionados'}
                  </Badge>
                </span>
              </div>
            </Card>

            {/* Sugeridos por equipo (con rating/comments inline cuando se seleccionan) */}
            {selectedTeams.length > 0 && (
              <Card className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-semibold text-gray-800">
                    {selectedTeams.length === 1 ? 'Jugadores del equipo' : 'Jugadores de los equipos'}
                  </Label>
                  <span className="text-xs text-gray-500">
                    {suggestedPlayers.length} disponibles
                  </span>
                </div>
                {suggestedPlayers.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center">
                    No hay jugadores registrados en {selectedTeams.join(' o ')} con estos filtros.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {suggestedPlayers.map((p) => (
                      <PlayerRow
                        key={p.id}
                        player={p}
                        observed={observed}
                        onToggle={togglePlayer}
                        onUpdate={updatePlayerField}
                        testIdPrefix="suggested"
                      />
                    ))}
                  </div>
                )}
              </Card>
            )}

            {/* Otros seleccionados (que no están en los equipos elegidos) */}
            {(() => {
              const suggestedIds = new Set(suggestedPlayers.map(p => p.id));
              const otherSelected = selectedPlayers.filter(p => !suggestedIds.has(p.id));
              if (otherSelected.length === 0) return null;
              return (
                <Card className="p-4">
                  <Label className="text-sm font-semibold text-gray-800 mb-3 block">
                    Otros seleccionados
                  </Label>
                  <div className="space-y-2">
                    {otherSelected.map((p) => (
                      <PlayerRow
                        key={p.id}
                        player={p}
                        observed={observed}
                        onToggle={togglePlayer}
                        onUpdate={updatePlayerField}
                        testIdPrefix="other-selected"
                      />
                    ))}
                  </div>
                </Card>
              );
            })()}

            {/* Buscar otros jugadores */}
            <Card className="p-4">
              <Label htmlFor="search-player" className="text-sm font-semibold text-gray-800 mb-2 block">
                Buscar otro jugador
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="search-player"
                  placeholder="Buscar por nombre..."
                  value={searchPlayer}
                  onChange={(e) => setSearchPlayer(e.target.value)}
                  className="pl-9 h-12 text-base"
                  data-testid="input-search-player"
                />
              </div>
              {searchPlayer && (
                <div className="mt-2 space-y-2">
                  {searchedPlayers.length === 0 ? (
                    <p className="p-3 text-sm text-gray-500 text-center">Sin coincidencias</p>
                  ) : (
                    searchedPlayers.map((p) => (
                      <PlayerRow
                        key={p.id}
                        player={p}
                        observed={observed}
                        onToggle={togglePlayer}
                        onUpdate={updatePlayerField}
                        testIdPrefix="search-result"
                      />
                    ))
                  )}
                </div>
              )}
            </Card>

            {/* Notas generales */}
            <Card className="p-4">
              <Label htmlFor="general-notes" className="text-sm font-semibold text-gray-800 mb-2 block">
                Observaciones generales (opcional)
              </Label>
              <Textarea
                id="general-notes"
                rows={3}
                placeholder="Contexto del evento, clima, nivel general, etc."
                value={generalNotes}
                onChange={(e) => setGeneralNotes(e.target.value)}
                data-testid="input-general-notes"
                className="text-base"
              />
            </Card>
          </div>
        )}

        {/* Sticky footer con acciones */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-3 flex gap-2 z-20 sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:mt-6 sm:justify-end">
          {step === 1 ? (
            <>
              <Button
                variant="outline"
                onClick={() => navigate('/seguimiento')}
                className="flex-1 sm:flex-none h-12"
                data-testid="btn-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => setStep(2)}
                disabled={!canProceed()}
                className="bg-brand-600 hover:bg-brand-700 flex-1 sm:flex-none h-12 text-base"
                data-testid="btn-next"
              >
                Siguiente
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 sm:flex-none h-12"
                data-testid="btn-prev"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Atrás
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || selectedPlayers.length === 0}
                className="bg-brand-600 hover:bg-brand-700 flex-1 sm:flex-none h-12 text-base"
                data-testid="btn-save"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Guardar')}
              </Button>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SeguimientoForm;
